import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { styled } from 'linaria/react';
import t from '@jetshop/intl';
import { theme } from '../../Theme';
import { ElevatePaginationDot } from './ElevatePaginationDot';
import { useElevate } from '../ElevateContext';

const ElevatePaginationWrapper = styled('nav')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 20px auto 0 auto;
  padding: 20px 20px;

  .summary {
    font-size: 1rem;
    color: ${theme.colors.black};
    margin-bottom: 12px;
  }

  .dots-wrapper {
    position: relative;
    display: flex;
    gap: 10px;
    margin: 20px 0;
    .dot-combiner {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 100%;
      height: 2px;
      background-color: #efefef;
    }
  }

  .show-more-btn {
    height: 40px;
    width: fit-content;
    padding: 0 24px;
    min-width: 250px;
    background: ${theme.colors.black};
    color: ${theme.colors.white};
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    padding-top: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
  }
`;

export const ElevateGridPagination = ({
  totalHits,
  pageChangeScrollAnchorId
}) => {
  const { queryStringToObject, objectToQueryString, PER_PAGE } = useElevate();

  const history = useHistory();
  const location = useLocation();
  const [renderPages, setRenderPages] = useState([]);
  const currentPage = parseInt(queryStringToObject(location.search)?.page) || 1;

  const totalPages = Math.ceil(totalHits / PER_PAGE);

  const firstPage = { page: 1 };
  const lastPage = { page: totalPages };
  const tempArray = Array.from({ length: totalPages }, (_, i) => i + 1);

  const hasNextPage = currentPage < totalPages;

  useEffect(() => {
    // Update the pagination pages when the location changes
    const generatePaginationPages = () => {
      const pages = [];
      if (totalPages > 0) {
        // Always add the first page object
        pages.push(firstPage);

        // Handle the case when current page is close to the start (Page 2 or 3)
        if (currentPage === 1 || currentPage === 2 || currentPage === 3) {
          for (let i = 1; i < Math.min(4, totalPages - 1); i++) {
            pages.push({ page: tempArray[i] });
          }

          if (totalPages > 4) {
            pages.push({ page: '...' });
          }
        }

        // Handle the case when current page is close to the end
        else if (currentPage >= lastPage.page - 2) {
          if (totalPages > 4) pages.push({ page: '...' });

          for (let i = totalPages - 4; i < totalPages - 1; i++) {
            if (i > 0) pages.push({ page: tempArray[i] });
          }
        }

        // Handle the case when current page is in the middle
        else {
          const previousPageObj = tempArray.find(p => p === currentPage - 1);
          const currentPageObj = tempArray.find(p => p === currentPage);
          const nextPageObj = tempArray.find(p => p === currentPage + 1);

          // Add ellipsis if there's a gap after the first page
          if (previousPageObj > firstPage.page + 1) {
            pages.push({ page: '...' });
          }

          // Add previous, current, and next page objects
          pages.push(
            { page: previousPageObj },
            { page: currentPageObj },
            { page: nextPageObj }
          );

          // Add ellipsis if there's a gap before the last page
          if (nextPageObj < lastPage.page - 1) {
            pages.push({ page: '...' });
          }
        }

        // Always add the last page object
        pages.push(lastPage);
      }
      return pages;
    };
    setRenderPages(generatePaginationPages());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, totalHits]);

  const getPageParams = page => {
    const params = queryStringToObject(location.search);

    if (page === 1) {
      delete params.page;
    } else {
      params.page = page;
    }

    return params;
  };

  const getPageLocation = page => ({
    pathname: location.pathname,
    search: objectToQueryString(getPageParams(page)),
    hash: ''
  });

  const scrollToListTop = () => {
    if (typeof document === 'undefined') return;

    const header = document.getElementById('header-container');
    const headerHeight = header?.getBoundingClientRect()?.height || 0;
    const listBeginning = document.getElementById('filter-and-sort');

    if (listBeginning) {
      const position =
        listBeginning.getBoundingClientRect().top +
        window.scrollY -
        headerHeight;
      window.scrollTo({ top: position, behavior: 'instant' });
      return;
    }

    window.scrollTo({ top: 200, behavior: 'instant' });
  };

  const handlePageNavigate = () => {
    // Drop hash first so category page doesn't treat this as a fresh top scroll
    if (location.hash) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: ''
      });
    }
    scrollToListTop();
  };

  return (
    <ElevatePaginationWrapper aria-label="Pagination" className="">
      <p className="summary">
        {t.rich('Showing: {from} - {to} of {total} products', {
          from: currentPage * PER_PAGE - PER_PAGE + 1,
          to:
            currentPage * PER_PAGE < totalHits
              ? currentPage * PER_PAGE
              : totalHits,
          total: totalHits
        })}
      </p>
      {hasNextPage && (
        <Link
          className="show-more-btn"
          to={getPageLocation(currentPage + 1)}
          onClick={() => handlePageNavigate(currentPage + 1)}
        >
          {t('Show more products')}
        </Link>
      )}

      <div className="dots-wrapper">
        {totalPages > 1 &&
          renderPages?.map((page, index) => {
            return (
              <ElevatePaginationDot
                key={`pagination-dot-${page?.page}-${index}`}
                value={page?.page}
                active={currentPage === page?.page}
                to={getPageLocation(page?.page)}
                onNavigate={handlePageNavigate}
              />
            );
          })}

        <div className="dot-combiner" />
      </div>
    </ElevatePaginationWrapper>
  );
};
